import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Shield,
  CheckCircle2,
  Flag,
  Trash2,
  Star,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { ListPageSkeleton } from "@/components/PageSkeleton";

type StatusFilter = "pending_flagged" | "PENDING" | "FLAGGED";

export default function ReviewsModeration() {
  const { user } = useAuth();
  const isSuperAdmin = user?.staffRole === "SUPER_ADMIN";
  const canAct = canWrite(user?.staffRole);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending_flagged");
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reasonAction, setReasonAction] = useState<"flag" | "delete">("flag");
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const LIMIT = 20;

  const utils = trpc.useUtils();

  const statusInput =
    statusFilter === "pending_flagged"
      ? (["PENDING", "FLAGGED"] as const)
      : ([statusFilter] as const);

  const { data, isLoading } = trpc.admin.reviews.listPendingReviews.useQuery({
    status: [...statusInput],
    page,
    limit: LIMIT,
  });

  const approveMutation = trpc.admin.reviews.approveReview.useMutation({
    onSuccess: () => {
      utils.admin.reviews.listPendingReviews.invalidate();
      toast.success("Avis approuvé");
    },
  });

  const flagMutation = trpc.admin.reviews.flagReview.useMutation({
    onSuccess: () => {
      utils.admin.reviews.listPendingReviews.invalidate();
      toast.success("Avis signalé");
      closeDialog();
    },
  });

  const deleteMutation = trpc.admin.reviews.deleteReview.useMutation({
    onSuccess: () => {
      utils.admin.reviews.listPendingReviews.invalidate();
      toast.success("Avis supprimé");
      closeDialog();
    },
  });

  function openReasonDialog(reviewId: string, action: "flag" | "delete") {
    setSelectedReviewId(reviewId);
    setReasonAction(action);
    setReason("");
    setReasonDialogOpen(true);
  }

  function closeDialog() {
    setReasonDialogOpen(false);
    setSelectedReviewId(null);
    setReason("");
  }

  function submitReason() {
    if (!selectedReviewId || !reason.trim()) return;
    if (reasonAction === "flag") {
      flagMutation.mutate({ reviewId: selectedReviewId, reason: reason.trim() });
    } else {
      deleteMutation.mutate({ reviewId: selectedReviewId, reason: reason.trim() });
    }
  }

  const reviews = data?.reviews ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  function formatDate(d: string | Date | null | undefined) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <ListPageSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Modération des avis
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Approuver, signaler ou supprimer les avis clients TravauxBTP
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="text-xs text-muted-foreground mb-1">En attente</div>
              <div className="text-2xl font-bold text-warning">{total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="text-xs text-muted-foreground mb-1">Page</div>
              <div className="text-2xl font-bold">
                {page}/{totalPages}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="text-xs text-muted-foreground mb-1">Filtre actif</div>
              <div className="text-lg font-bold capitalize">
                {statusFilter === "pending_flagged" ? "En attente + Signalés" : statusFilter}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center gap-3">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as StatusFilter);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending_flagged">En attente + Signalés</SelectItem>
              <SelectItem value="PENDING">En attente seulement</SelectItem>
              <SelectItem value="FLAGGED">Signalés seulement</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground ml-auto">
            {total} avis
          </span>
        </div>

        <Card>
          <CardContent className="p-0">
            {reviews.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-sm">
                Aucun avis à modérer
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Auteur</TableHead>
                    <TableHead>Artisan</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Projet</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.map((review: any) => (
                    <TableRow key={review.id}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(review.createdAt)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {review.Author
                          ? `${review.Author.firstName ?? ""} ${review.Author.lastName ?? ""}`.trim() ||
                            review.Author.email
                          : "—"}
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {review.ArtisanCompany?.name ?? "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3.5 w-3.5 ${
                                i < (review.rating ?? 0)
                                  ? "text-warning fill-warning"
                                  : "text-muted-foreground/30"
                              }`}
                            />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            review.moderationStatus === "FLAGGED"
                              ? "destructive"
                              : "outline"
                          }
                          className="text-xs"
                        >
                          {review.moderationStatus === "FLAGGED"
                            ? "Signalé"
                            : "En attente"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                        {review.PrivateProject?.title ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {canAct ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                approveMutation.mutate({ reviewId: review.id })
                              }
                              disabled={approveMutation.isPending}
                              title="Approuver"
                            >
                              <CheckCircle2 className="h-4 w-4 text-success" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openReasonDialog(review.id, "flag")}
                              title="Signaler"
                            >
                              <Flag className="h-4 w-4 text-warning" />
                            </Button>
                            {isSuperAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openReasonDialog(review.id, "delete")}
                                title="Supprimer"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Lecture seule</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Précédent
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Suivant
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      <Dialog open={reasonDialogOpen} onOpenChange={setReasonDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reasonAction === "flag"
                ? "Signaler cet avis"
                : "Supprimer cet avis"}
            </DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Raison..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Annuler
            </Button>
            <Button
              variant={reasonAction === "delete" ? "destructive" : "default"}
              onClick={submitReason}
              disabled={
                !reason.trim() ||
                flagMutation.isPending ||
                deleteMutation.isPending
              }
            >
              {reasonAction === "flag" ? "Signaler" : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
